import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import {
  DATA_ACCESS_ALL_STATUS,
  DATA_ACCESS_DISCLAIMER_PLACEHOLDER,
  QueryKeys,
} from "@/constants";
import IDENTIFIERS from "@/constants/test_id";
import useAuth from "@/context/auth-provider";
import { useDataAccessUpdateMutation } from "@/hooks/use-data-access";
import { queryClient } from "@/hooks/use-query-client";
import { sanitizeHtmlWithPlaceholders } from "@/lib/data-access-utils";
import type { DataAccessConfigResponseType } from "@/model/data-access";
import type { Demographics, Relation } from "@/model/demographics";
import {
  LogCategories,
  LogSubCategories,
  LogTags,
} from "@/services/logger/logger-types";
import { dequeueAppAlertDialog } from "@/store/app-alert-dialog/app-alert-dialog-slice";
import type { DataAccessUpdatePayload } from "@/types";
import { useIsRestoring } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { LoaderCircle } from "lucide-react";
import { useState } from "react";
import { useDispatch } from "react-redux";

type AuthRevokeDisclaimerProps = {
  grantedTo: string;
  contentType: string;
};

const AuthRevokeDisclaimer: React.FC<AuthRevokeDisclaimerProps> = ({
  contentType,
  grantedTo,
}) => {
  const { authData } = useAuth();
  const isRestoring = useIsRestoring();
  const dispatch = useDispatch();
  const demographics = queryClient.getQueryData([
    QueryKeys.DEMOGRAPHICS,
    authData?.user?.username,
  ]) as Demographics;
  const members = queryClient.getQueryData([QueryKeys.MEMBERS]) as Record<
    string,
    Relation
  >;
  const dataAccessDisclaimerContents = queryClient.getQueryData([
    QueryKeys.DATA_ACCESS_CONFIG,
  ]) as DataAccessConfigResponseType;
  const content =
    contentType &&
    !isRestoring &&
    dataAccessDisclaimerContents.authorization[contentType];
  const placeholderReplacement = !isRestoring
    ? `${members[grantedTo].firstName} ${members[grantedTo].lastName}`
    : "";
  const dataAccessUpdateMutation = useDataAccessUpdateMutation();
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  // 3.3.4 Error Prevention (Legal, Financial, Data) — AA / 4.1.3 Status Messages — AA:
  // surfaced and announced on failure instead of the dialog silently closing.
  const [updateError, setUpdateError] = useState<string | null>(null);

  const onClose = () => {
    dispatch(dequeueAppAlertDialog());
  };

  const onRevert = () => {
    setUpdateError(null);
    setIsUpdating(true);
    const revertPayload: DataAccessUpdatePayload = {
      memberId: demographics.memberId,
      grantedBy: demographics.memberId,
      grantedTo,
      status: DATA_ACCESS_ALL_STATUS.REVOKED,
      token: authData!.user!.access_token,
    };
    dataAccessUpdateMutation.mutate(
      {
        ...revertPayload,
      },
      {
        onSuccess: () => {
          setIsUpdating(false);
          dispatch(dequeueAppAlertDialog());
        },
        onError: () => {
          setIsUpdating(false);
          setUpdateError(
            "Something went wrong and this wasn't saved. Please try again.",
          );
        },
      },
    );
  };

  return (
    !isRestoring && (
      <Dialog
        defaultOpen={true}
        onOpenChange={onClose}
        logTag={LogTags.MODULE_ACCESS}
        logCategory={LogCategories.DATA_ACCESS_MANAGEMENT}
        logSubCategory={LogSubCategories.ACCESS_REVOKE_DISCLAIMER}
        logDialogName={LogSubCategories.ACCESS_REVOKE_DISCLAIMER}
      >
        <DialogContent
          className="DialogContent"
          logDialogName={LogSubCategories.ACCESS_REVOKE_DISCLAIMER}
        >
          <DialogTitle className="DialogTitle">Disclaimer</DialogTitle>
          <DialogDescription className="DialogDescription">
            {content && (
              <span
                className="text-sm text-muted-foreground"
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtmlWithPlaceholders(
                    content,
                    DATA_ACCESS_DISCLAIMER_PLACEHOLDER.MEMBER_NAME,
                    placeholderReplacement,
                  ),
                }}
              />
            )}
          </DialogDescription>
          {/* 4.1.3 Status Messages — AA: mounted unconditionally so the error is
              announced the moment it appears, not silently added to an absent region. */}
          {updateError && (
            <p role="alert" className="text-sm text-destructive">
              {updateError}
            </p>
          )}
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
            <DialogClose asChild disabled={isUpdating}>
              <Button
                variant="outline"
                className="w-32 rounded-sm"
                disabled={isUpdating}
                data-testid={IDENTIFIERS.ACCESS_REVOKE_DISCLAIMER.CANCEL_BUTTON}
              >
                Cancel
              </Button>
            </DialogClose>
            <DialogClose
              asChild
              disabled={isUpdating}
              onClick={(event) => {
                // Keep the dialog open until the mutation actually resolves —
                // DialogClose closes by default on click.
                event.preventDefault();
                onRevert();
              }}
            >
              <Button
                variant="default"
                className="w-32 rounded-sm hover:bg-primary-selected"
                disabled={isUpdating}
                data-testid={IDENTIFIERS.ACCESS_REVOKE_DISCLAIMER.REVERT_BUTTON}
                logMetadata={{
                  grantedBy: demographics.memberId,
                  grantedTo,
                  status: DATA_ACCESS_ALL_STATUS.REVOKED,
                }}
              >
                {isUpdating ? (
                  <LoaderCircle className="text-white-500 size-6 animate-spin" />
                ) : (
                  "Continue to Revert"
                )}
              </Button>
            </DialogClose>
          </div>
        </DialogContent>
      </Dialog>
    )
  );
};

export default AuthRevokeDisclaimer;
